import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import {FormattedMessage} from 'react-intl';

import FormattedLink from '@stubhub/react-formatted-link';
import Spinner from '@stubhub/react-spinner';
import {ssr_wait} from '@stubhub/react-store-provider';

import Pagination from '../react-pagination';

import './index.scss';

class ChildSEOLinks extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      currentPage: props.currentPage,
    };
  }

  @ssr_wait
  init() {
    return this.loadEntitySEOLinks(this.props);
  }

  getInstanceId = (entityId, entityType) => `${entityType}-${entityId}`;

  /**
   * Wrapper Method for `loadEntitySEOLinks` used to fetch and initialize the component.
   * @param props [object] The Props to be used to fetch the SEO Links from the API.
   * @returns loadEntitySEOLinks [Promise] The Promise returned from the Catalog API fetch.
   *
   */
  loadEntitySEOLinks = (props) => {
    const currentProps = {...props};
    const {currentPage} = this.state;
    const {loadEntitySEOLinks} = this.props;
    currentProps.currentPage = currentPage;

    return loadEntitySEOLinks && loadEntitySEOLinks(currentProps);
  };

  /**
   * Method to handle the Page Selection on the Pagination Bar.
   * @param pageNumber [number] The Page number selected on the Pagination bar.
   *
   */
  onPageChange = (pageNumber) => {
    this.setState({currentPage: pageNumber}, () => this.loadEntitySEOLinks(this.props));
  };

  /**
   * React Lifecycle Method
   *
   */
  UNSAFE_componentWillMount() {
    const {seoLinks, instanceId, entityId, entityType} = this.props;
    if (
      (!seoLinks || seoLinks.length === 0) &&
      (!instanceId || instanceId !== this.getInstanceId(entityId, entityType))
    ) {
      this.init();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {instanceId} = this.props;
    const {instanceId: nextInstanceId, loading, entityId, entityType} = nextProps;

    /**
     * If entityId/entityType has changed then reload the data with new entityId/entityType.
     *
     */
    if ((instanceId && instanceId !== this.getInstanceId(entityId, entityType)) || (!loading && !nextInstanceId)) {
      this.setState({currentPage: 1}, () => this.loadEntitySEOLinks(nextProps));
    }
  }

  componentWillUnmount() {
    const {resetChildSEOLinks} = this.props;

    /**
     * Reset Child links when Component is unmounted.
     *
     */
    resetChildSEOLinks && resetChildSEOLinks();
  }

  render() {
    /* istanbul ignore next */
    const {
      seoLinks = [],
      title,
      className,
      showPagination,
      numFound,
      loading,
      disableNoLinksFoundMessage,
      hideSEOLinks,
    } = this.props;
    const {currentPage} = this.state;
    let seoLinksSection;

    /**
     * Show Spinner when loading.
     *
     */
    if (loading) {
      seoLinksSection = <Spinner />;
    } else if (seoLinks.length > 0) {
      /**
       * Show SEO Links when Links are Available.
       *
       */
      seoLinksSection = seoLinks.map((seoLink = {}) => {
        return (
          <li key={seoLink.id}>
            <FormattedLink className="ChildSEOLinks__link" href={seoLink.webURI}>
              {seoLink.name}
            </FormattedLink>
          </li>
        );
      });
      seoLinksSection = <ul className="ChildSEOLinks__links">{seoLinksSection}</ul>;
    } else if (!disableNoLinksFoundMessage) {
      /**
       * Show Default Links when Links are not Available.
       *
       */
      seoLinksSection = (
        <FormattedMessage
          id="ChildSEOLinks.EmptyLinksText"
          defaultMessage="Sorry, we have no categories listed at this time."
          description="The Placeholder Text to be displayed in case "
        />
      );
    }

    return seoLinksSection && !hideSEOLinks ? (
      <section className={cs('ChildSEOLinks__Container', className)}>
        {title && <div className="ChildSEOLinks__title">{title}</div>}
        <div className="ChildSEOLinks__SEOLinkSection">{seoLinksSection}</div>
        {showPagination && (
          <Pagination
            className="ChildSEOLinks__Pagination"
            numFound={numFound}
            resultsPerPage={60}
            onPageChange={this.onPageChange}
            currentPage={currentPage}
          />
        )}
      </section>
    ) : null;
  }
}

ChildSEOLinks.contextTypes = {
  queue: PropTypes.object,
};

ChildSEOLinks.propTypes = {
  /**
   * The type of the child SEO link to be displayed by the component
   *
   */
  entityType: PropTypes.string,

  /**
   * The ID of the Entity of the parent which should be used in the search specification.
   *
   */
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
   * The Title to be displayed in the Heading Section of the Component
   *
   */
  title: PropTypes.node.isRequired,

  /**
   * The SEO Links array fectch from the Catalog API for the current Search Spec.
   *
   */
  seoLinks: PropTypes.array,

  /**
   * The Total Number of the SEO Links found in the Catalog API for the current Search Spec.
   *
   */
  numFound: PropTypes.number,

  /**
   * Controller Method to fetch the SEO Link from the API.
   *
   */
  loadEntitySEOLinks: PropTypes.func,

  /**
   * Flag to indicate enable pagination on the component.
   *
   */
  showPagination: PropTypes.bool,

  /**
   * Prop to pass the number of the page externally.
   *
   */
  currentPage: PropTypes.number,

  /**
   * Prop indicating the number of the results that should be display on one page.
   *
   */
  // eslint-disable-next-line react/no-unused-prop-types
  resultsPerPage: PropTypes.number,

  /**
   * Flag indicating if the API is loading Data.
   *
   */
  loading: PropTypes.bool,

  /**
   * Flag to Disable the Message Shown when links are not are not Available
   *
   */
  disableNoLinksFoundMessage: PropTypes.bool,

  /**
   * The Unique Identifier Used to Detect if the Component has received new props since it fetched the data from the server.
   *
   */
  instanceId: PropTypes.string,

  /**
   * Hide SEO Links from rendering
   *
   */
  hideSEOLinks: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  ssr: PropTypes.bool,

  /**
   * Reset Child links function
   */
  resetChildSEOLinks: PropTypes.func,

  /**
   * Css class name
   */
  className: PropTypes.string,
};

ChildSEOLinks.defaultProps = {
  resultsPerPage: 500,
  showPagination: false,
  currentPage: 1,
  ssr: true,
};

export default ChildSEOLinks;
